import { useEffect, useState } from "react";
import { FirestoreService } from "../../../../../api/firebase/firestoreService";
import { FirestoreDocumentReference } from "../../../../../api/firebase/references";
import { capitalizeWordsSeperatedByUnderScore } from "../../../../../utils/stringModifications";

/** Single org tab rendered above the NAR Agent Grid. */
export interface NarAgentGridOrgTab {
  id: string;
  label: string;
}

/** One row from Firestore `client_wise_config`. */
interface ClientWiseConfigEntry {
  client?: string;
  client_id?: string;
  autopass_review?: boolean;
  doc_upload?: boolean;
  mode?: string;
}

/** Shape of `/configurations/auth_grid/client_wise_config/latest`. */
interface ClientWiseConfigDocument {
  client_wise_config?: ClientWiseConfigEntry[];
}

/**
 * Formats a Firestore `client` name for tab display.
 *
 * Replaces underscores with spaces and capitalizes each word
 * (e.g. `NYCBS_stat` → `NYCBS Stat`).
 *
 * @param clientName - Raw `client` value from client_wise_config
 * @returns Human-readable tab label
 */
const formatClientTabLabel = (clientName: string): string =>
  capitalizeWordsSeperatedByUnderScore(clientName.trim()).toUpperCase();

/**
 * Loads NAR Agent Grid org tabs from Firestore client_wise_config.
 *
 * Uses each row's `client_id` as the tab id (API `client_id`) and a
 * cleaned-up `client` name as the display label.
 *
 * @returns Org tabs, loading flag, and optional fetch error message
 */
const useNarAgentGridOrgTabs = (): {
  tabs: NarAgentGridOrgTab[];
  loading: boolean;
  error: string | null;
} => {
  const [tabs, setTabs] = useState<NarAgentGridOrgTab[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchOrgTabs = async () => {
      setLoading(true);
      setError(null);
      try {
        const doc =
          await FirestoreService.getDocument<ClientWiseConfigDocument>(
            FirestoreDocumentReference.narClientWiseConfig(),
          );
        const entries = doc?.client_wise_config ?? [];
        const nextTabs = entries
          .filter(
            (entry): entry is ClientWiseConfigEntry & { client_id: string } =>
              typeof entry?.client_id === "string" &&
              entry.client_id.length > 0,
          )
          .map((entry) => ({
            id: entry.client_id,
            label: formatClientTabLabel(
              entry.client?.trim() || entry.client_id,
            ),
          }));
        setTabs(nextTabs);
      } catch (err) {
        console.error("Error fetching NAR Agent Grid org tabs:", err);
        setError("Failed to load organization tabs.");
        setTabs([]);
      } finally {
        setLoading(false);
      }
    };

    fetchOrgTabs();
  }, []);

  return { tabs, loading, error };
};

export default useNarAgentGridOrgTabs;
